import { ChevronRight, ShoppingBag } from "lucide-react";
import { Button } from "@/components/atoms/button";
import { useOrdersStore } from "./use-orders-store";
import { AlertCircle } from "lucide-react";
import OrderDetailInformation from "./order-detail-information";
import OrderDetailPayment from "./order-detail-payment";
import OrderDetailDriver from "./order-detail-driver";
import OrderStateHistory from "./order-state-history";
import OrderStatusChange from "./order-status-change";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useOrder } from "@/hooks/useOrder";

export default function OrderDetail() {
  const orderDetail = useOrdersStore((state) => state.orderDetail);
  const isDetailLoading = useOrdersStore((state) => state.isDetailLoading);
  const orderNotFound = useOrdersStore((state) => state.orderNotFound);
  const setOrderDuration = useOrdersStore((state) => state.setOrderDuration);
  const setOrderDetail = useOrdersStore((state) => state.setOrderDetail);
  const fetchOrderById = useOrdersStore((state) => state.fetchOrderById);
  const navigate = useNavigate();
  const { fetchOrderCompletationTime } = useOrder(
    orderDetail?.id.toString() || null,
  );

  const { orderId } = useParams<{ orderId: string }>();
  useEffect(() => {
    if (!orderId) return;
    const id = Number(orderId);
    if (Number.isNaN(id)) return;
    setOrderDetail(id);
    fetchOrderById(id);
  }, [orderId, setOrderDetail, fetchOrderById]);
  useEffect(() => {
    if (orderDetail) {
      const completationTime = fetchOrderCompletationTime(
        orderDetail?.id.toString() || "",
      );
      completationTime.then((res) => {
        setOrderDuration(res);
      });
    }
  }, [orderDetail]);

  return (
    <section className="min-h-screen">
      <div className="max-w-7xl">
        <header className="bg-white px-6 py-4 flex justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-medium text-gray-800">
              {orderDetail
                ? `Orden ${orderDetail?.tracking_id}`
                : "Detalle de orden"}
            </h1>
            <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600">
              <Button
                variant="link"
                onClick={() => {
                  navigate("/orders");
                }}
                className="text-gray-400 hover:text-indigo-600 flex items-center gap-1 p-0 break-all group"
              >
                <ShoppingBag
                  size={18}
                  className="text-gray-400 group-hover:text-indigo-600"
                />
                Órdenes
              </Button>
              <ChevronRight
                size={16}
                className="text-gray-400 group-hover:text-indigo-600"
              />
              <span className="font-medium break-words">
                Tracking {orderDetail?.tracking_id}
              </span>
            </div>
          </div>
        </header>

        <div className="p-6 bg-gray-100 space-y-4">
          {isDetailLoading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2" />
              Cargando detalle de la orden...
            </div>
          )}

          {!isDetailLoading && orderNotFound && (
            <div className="flex items-center justify-center py-12 text-red-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              No se encontró información de la orden solicitada
            </div>
          )}

          {!isDetailLoading && orderDetail && (
            <OrderStatusChange order={orderDetail} />
          )}

          <section className="bg-white rounded-2xl px-6 py-8 shadow-md">
            <div className="flex flex-col lg:flex-row gap-6">
              {!isDetailLoading && orderDetail && (
                <>
                  <OrderDetailInformation order={orderDetail} />
                  <OrderDetailPayment order={orderDetail} />
                </>
              )}
            </div>
            <div className="mt-6">
              {!isDetailLoading && orderDetail && (
                <OrderDetailDriver order={orderDetail} />
              )}
            </div>
          </section>

          <div className="mt-6">
            {!isDetailLoading && orderDetail && (
              <OrderStateHistory order={orderDetail} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
